<template>
  <section
    class="relative mt-6 md:mt-7 rounded-xl border border-boba-grey/50 bg-boba-sand-light shadow-sm px-5 pt-8 pb-5 md:px-8 md:pt-8 md:pb-6"
  >
    <scoreboard-countdown
      v-if="countdownTarget"
      class="absolute right-5 -top-5 md:right-8 md:-top-6"
      :to="countdownTarget"
      :key="countdownTarget.toMillis()"
      @ended="emit('ended')"
    >
      <template #label>
        <p
          class="flex items-center gap-x-1.5 md:justify-start md:pb-1 text-sm leading-none"
        >
          <i class="fa-solid fa-clock text-sm md:text-base" aria-hidden="true" />
          <span>{{ countdownLabel }}</span>
        </p>
      </template>
    </scoreboard-countdown>

    <div class="flex flex-wrap items-baseline gap-x-3 gap-y-1 md:pr-40">
      <h2 class="text-xl md:text-2xl font-semibold tracking-tight text-boba-brown">
        Blind Dutch Auction
      </h2>
      <button
        data-test="how-it-works"
        type="button"
        class="inline-flex items-center gap-1.5 rounded text-sm font-medium text-boba-grey-dark hover:text-boba-brown focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-boba-brown focus-visible:ring-offset-2 focus-visible:ring-offset-boba-sand-light min-h-[44px] md:min-h-0"
        @click="howItWorks?.open()"
      >
        <i class="fa-regular fa-circle-question" aria-hidden="true" />
        How it works
      </button>
    </div>
    <p class="mt-1.5 text-sm text-boba-grey-dark max-w-prose">
      Enter the most you'd pay per item. Every winner pays the same lowest
      winning price, so you'll usually pay less.
    </p>

    <how-it-works-dialog ref="howItWorks" :listing="listing" />

    <div class="mt-4 border-t border-boba-grey/50"></div>

    <slot name="form">
      <form class="mt-4" @submit.prevent="place" novalidate>
        <div
          class="grid grid-cols-[minmax(0,1fr)_6.5rem] gap-x-3 gap-y-3 items-start lg:items-end lg:grid-cols-[minmax(0,1fr)_7.5rem_minmax(11rem,0.9fr)] lg:gap-x-4"
        >
          <div>
            <div class="flex items-baseline justify-between gap-x-2 mb-1.5">
              <label
                for="max-bid-amount"
                class="text-xs font-semibold uppercase tracking-wide text-boba-grey-dark whitespace-nowrap"
              >
                Max bid per item
              </label>
              <span
                v-if="minBid !== null && listing.hasPublicFloor()"
                data-test="floor-hint"
                class="hidden lg:inline text-xs text-boba-grey-dark tabular-nums whitespace-nowrap"
              >
                Min {{ $format(minBid / MILE) }} {{ minBidUnit }}/ea
              </span>
            </div>
            <div
              class="flex h-12 items-center rounded-lg border border-boba-grey bg-white pr-3 overflow-hidden focus-within:border-boba-brown focus-within:ring-2 focus-within:ring-boba-brown/20"
              :class="{ 'bg-boba-sand-light text-boba-grey-dark': inputsDisabled }"
            >
              <input
                id="max-bid-amount"
                data-test="amount"
                type="text"
                inputmode="decimal"
                autocomplete="off"
                v-model="amountInput"
                :disabled="inputsDisabled"
                class="h-full w-full min-w-0 bg-transparent px-3 text-lg tabular-nums placeholder:text-boba-grey placeholder:font-light outline-none disabled:cursor-not-allowed"
                placeholder="Your bid"
              />
              <span class="text-sm text-boba-grey-dark select-none">miles</span>
            </div>
            <p
              v-if="minBid !== null && listing.hasPublicFloor()"
              class="mt-1 text-xs text-boba-grey-dark tabular-nums lg:hidden"
            >
              Min {{ $format(minBid / MILE) }} {{ minBidUnit }}/ea
            </p>
            <p
              v-if="amountErrorText"
              data-test="amount-error"
              class="mt-2 flex items-start gap-1.5 text-sm text-red-700"
            >
              <i class="fa-solid fa-circle-exclamation mt-0.5" aria-hidden="true" />
              <span>{{ amountErrorText }}</span>
            </p>
          </div>

          <div>
            <div class="flex items-baseline justify-between gap-x-2 mb-1.5">
              <label
                for="max-bid-quantity"
                class="text-xs font-semibold uppercase tracking-wide text-boba-grey-dark"
              >
                Quantity
              </label>
              <span
                v-if="quantityCap !== null && quantityCap > 0"
                class="text-xs text-boba-grey-dark tabular-nums"
              >
                Limit {{ quantityCap }}
              </span>
            </div>
            <select
              v-if="hasPublicLimit"
              id="max-bid-quantity"
              data-test="quantity"
              v-model="quantityInput"
              :disabled="inputsDisabled"
              class="h-12 w-full rounded-lg border border-boba-grey bg-white px-3 pr-8 text-lg tabular-nums focus:border-boba-brown focus:ring-2 focus:ring-boba-brown/20 outline-none disabled:bg-boba-sand-light disabled:text-boba-grey-dark disabled:cursor-not-allowed"
            >
              <option value=""></option>
              <option v-for="qty in selectOptions" :key="qty" :value="qty">
                {{ qty }}
              </option>
            </select>
            <div
              v-else
              class="flex h-12 items-center rounded-lg border border-boba-grey bg-white overflow-hidden focus-within:border-boba-brown focus-within:ring-2 focus-within:ring-boba-brown/20"
              :class="{ 'bg-boba-sand-light text-boba-grey-dark': inputsDisabled }"
            >
              <input
                id="max-bid-quantity"
                data-test="quantity"
                type="tel"
                inputmode="numeric"
                autocomplete="off"
                v-model="quantityInput"
                :disabled="inputsDisabled"
                class="h-full w-full min-w-0 bg-transparent px-3 text-lg tabular-nums placeholder:text-boba-grey placeholder:font-light outline-none disabled:cursor-not-allowed"
                placeholder="1"
              />
            </div>
            <p
              v-if="quantityErrorText"
              data-test="quantity-error"
              class="mt-2 flex items-start gap-1.5 text-sm text-red-700"
            >
              <i class="fa-solid fa-circle-exclamation mt-0.5" aria-hidden="true" />
              <span>{{ quantityErrorText }}</span>
            </p>
          </div>

          <button
            data-test="place-bid"
            type="button"
            @click="place"
            class="col-span-2 lg:col-span-1 h-12 w-full rounded-lg bg-boba-brown px-6 text-base md:text-lg font-semibold text-white hover:bg-boba-brown/90 active:bg-boba-brown focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-boba-brown focus-visible:ring-offset-2 focus-visible:ring-offset-boba-sand-light disabled:bg-boba-grey/60 disabled:text-boba-brown/60 disabled:shadow-none disabled:cursor-not-allowed transition-colors duration-150"
            :disabled="!canPlace"
          >
            <span v-if="!posting">Place bid</span>
            <span v-else
              >Placing <i class="fa-solid fa-spinner fa-spin-pulse" aria-hidden="true"
            /></span>
          </button>
        </div>

        <p
          v-if="quantityCap === 0"
          data-test="limit-reached"
          class="mt-3 inline-flex items-center gap-2 text-sm text-boba-grey-dark"
        >
          <i class="fa-regular fa-circle-check" aria-hidden="true" />
          You've reached your limit for this auction.
        </p>
        <p v-if="total !== null" class="mt-3 text-sm text-boba-grey-dark">
          Up to
          <b class="font-semibold text-boba-brown">{{ $format(fromBaseUnits(total)) }} miles</b>
          total. Nothing is charged until the auction closes; winners pay the
          final clearing price.
        </p>
        <p
          v-if="lowBalance"
          class="mt-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
        >
          <i class="fa-solid fa-triangle-exclamation mt-0.5" aria-hidden="true" />
          <span>
            Your balance ({{ $format(balance / 1e9) }} miles) is below this bid's
            maximum. Make sure you have enough miles when the auction closes.
          </span>
        </p>
        <p
          v-if="formError"
          data-test="form-error"
          class="mt-2 flex items-start gap-1.5 text-sm text-red-700"
        >
          <i class="fa-solid fa-circle-exclamation mt-0.5" aria-hidden="true" />
          <span>{{ formError }}</span>
        </p>
        <p
          v-if="disabled"
          class="mt-4 inline-flex items-center gap-2 text-sm text-boba-grey-dark"
        >
          <i class="fa-regular fa-lock" aria-hidden="true" />
          Bidding has closed.
        </p>
      </form>
    </slot>
  </section>
</template>
<script lang="ts" setup>
import { computed, ref } from "vue";
import { DateTime } from "ts-luxon";
import { Errors, Listing } from "@/types";
import ScoreboardCountdown from "@/components/ScoreboardCountdown.vue";
import HowItWorksDialog from "@/components/Listing/HowItWorksDialog.vue";
import { MILE, fromBaseUnits, mulBaseUnits, toBaseUnits } from "@/util/miles";
import currencyFormat from "@/util/currencyFormat";

const props = withDefaults(
  defineProps<{
    listing: Listing;
    balance: number;
    disabled?: boolean;
    posting?: boolean;
    error?: Errors.BidError | null;
    /** Override the countdown target (defaults to the auction end). */
    countdownTo?: DateTime | null;
    countdownLabel?: string;
  }>(),
  {
    disabled: false,
    posting: false,
    error: null,
    countdownTo: undefined,
    countdownLabel: "Auction ends in",
  },
);

const emit = defineEmits<{
  (e: "bid", amountBaseUnits: number, quantity: number): void;
  (e: "ended"): void;
}>();

const amountInput = ref("");
const quantityInput = ref<string | number>("");

const howItWorks = ref<InstanceType<typeof HowItWorksDialog> | null>(null);

const minBid = computed(() => props.listing.minBid());
/** A public per-bid limit worth rendering as a select; -1 and 0 are not. */
const hasPublicLimit = computed(() => (props.listing.buy_limit ?? 0) > 0);
const minBidUnit = computed(() => (minBid.value === MILE ? "mile" : "miles"));
const inputsDisabled = computed(() => props.disabled || minBid.value === null);
const countdownTarget = computed(
  () => props.countdownTo ?? props.listing.auctionEndsAt(),
);

/** Per-bid quantity cap, or null when unlimited. Active bids never reduce it. */
const quantityCap = computed<number | null>(() => {
  const user = props.listing.user_buy_limit;
  if (user === -1) {
    return null;
  }
  const cap = user ?? props.listing.buy_limit ?? null;
  return cap === null || cap < 0 ? null : cap;
});

const selectOptions = computed(() => {
  const max = quantityCap.value ?? props.listing.buy_limit ?? 0;
  return Array.from({ length: max }, (_, i) => i + 1);
});

const amountBaseUnits = computed(() => toBaseUnits(amountInput.value.trim()));

const localAmountError = computed(() => {
  const raw = amountInput.value.trim();
  if (!raw) {
    return null;
  }
  if (amountBaseUnits.value === null) {
    if (/^\d+\.\d{10,}$/.test(raw.replace(/,/g, ""))) {
      return "Use up to 9 decimal places.";
    }
    return "Enter a valid amount.";
  }
  if (minBid.value !== null && amountBaseUnits.value < minBid.value) {
    return `Minimum bid is ${currencyFormat(minBid.value / MILE)} ${minBidUnit.value}/ea.`;
  }
  return null;
});

const quantity = computed<number | null>(() => {
  const raw = String(quantityInput.value ?? "").trim();
  if (!raw) {
    return null;
  }
  return /^\d+$/.test(raw) ? parseInt(raw, 10) : NaN;
});

const localQuantityError = computed(() => {
  if (quantity.value === null) {
    return null;
  }
  if (Number.isNaN(quantity.value) || quantity.value < 1) {
    return "Enter a whole number of 1 or more.";
  }
  if (quantityCap.value !== null && quantity.value > quantityCap.value) {
    return `Limit ${quantityCap.value} per bid.`;
  }
  return null;
});

const amountErrorText = computed(
  () => localAmountError.value ?? props.error?.fields?.amount?.[0] ?? null,
);
const quantityErrorText = computed(
  () => localQuantityError.value ?? props.error?.fields?.quantity?.[0] ?? null,
);
const formError = computed(() => {
  if (!props.error) {
    return null;
  }
  const hasFieldErrors = Object.keys(props.error.fields ?? {}).length > 0;
  return hasFieldErrors ? null : props.error.message;
});

const fieldsValid = computed(
  () =>
    minBid.value !== null &&
    amountBaseUnits.value !== null &&
    !localAmountError.value &&
    quantity.value !== null &&
    !Number.isNaN(quantity.value) &&
    !localQuantityError.value,
);

const total = computed(() =>
  fieldsValid.value
    ? mulBaseUnits(amountBaseUnits.value as number, quantity.value as number)
    : null,
);

const lowBalance = computed(
  () =>
    total.value !== null &&
    BigInt(total.value) > BigInt(Math.max(0, Math.floor(props.balance ?? 0))),
);

const canPlace = computed(
  () => fieldsValid.value && !props.disabled && !props.posting,
);

function place() {
  if (!canPlace.value) {
    return;
  }
  emit("bid", amountBaseUnits.value as number, quantity.value as number);
}
</script>
